import { useCallback, useEffect, useState } from "react";
import type { LeagueCandidate, LeagueRoster } from "@tnp/contracts";
import Button from "../../../../shared/components/Button";
import Toast from "../../../../shared/components/Toast";
import { useLeagueResource } from "../../shared/hooks/useLeagueResource";
import { getLeagueRoster, saveLeagueRoster } from "../../shared/services/leagueMembershipService";
import { moveLeagueMember } from "../../shared/utils/leagueRoster";
import { getLeagueRosterTexts } from "../../shared/leagueRosterTexts";
import LeagueFeedback from "../../shared/components/LeagueFeedback";
import LeagueLoadError from "../../shared/components/LeagueLoadError";
import LeagueAthleteSearch from "./LeagueAthleteSearch";
import LeagueRosterList from "./LeagueRosterList";
import LeagueRoundPreview from "./LeagueRoundPreview";

type Props = { token: string; leagueId: string; divisionId: string; locked: boolean; onEditingChange?: (editing: boolean) => void };
function RosterLoaded({ token, leagueId, divisionId, locked, initial, reload, onEditingChange }: Props & { initial: LeagueRoster; reload: () => void }) {
  const t = getLeagueRosterTexts();
  const [roster, setRoster] = useState(initial);
  const [members, setMembers] = useState(initial.members);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<{ message: string; variant: "success" | "error" } | null>(null);
  const dirty = members.map(m => m.userId).join(",") !== roster.members.map(m => m.userId).join(",");
  useEffect(() => {
    onEditingChange?.(dirty || busy);
    return () => onEditingChange?.(false);
  }, [dirty, busy, onEditingChange]);
  function add(candidate: LeagueCandidate) {
    setMembers(previous => previous.length >= 256 || previous.some(member => member.userId === candidate.id) ? previous : [...previous, { userId: candidate.id, name: candidate.name, category: candidate.category, seed: previous.length + 1 }]);
  }
  async function save() {
    if (busy || locked) return;
    setBusy(true);
    try {
      const result = await saveLeagueRoster(token, leagueId, divisionId, { revision: roster.revision, userIds: members.map(m => m.userId) });
      setRoster(result); setMembers(result.members); setToast({ message: t.saved, variant: "success" });
    } catch (error) { setToast({ message: error instanceof Error ? error.message : t.error, variant: "error" }); }
    finally { setBusy(false); }
  }
  return <>
    {locked && <LeagueFeedback>{t.locked}</LeagueFeedback>}
    <fieldset className="league-roster-controls" disabled={busy}>
      {!locked && <LeagueAthleteSearch key={roster.revision} token={token} leagueId={leagueId} divisionId={divisionId} selectedIds={members.map(m => m.userId)} onAdd={add} />}
      <LeagueRosterList members={members} locked={locked} onMove={(index, direction) => setMembers(previous => moveLeagueMember(previous, index, direction))} onRemove={id => setMembers(previous => previous.filter(m => m.userId !== id))} />
      {(members.length > 0 || dirty) && <div className="league-actions">
        {!locked && <Button type="button" onClick={save} disabled={!dirty}>{busy ? t.saving : t.save}</Button>}
        <Button type="button" variant="ghost" onClick={reload}>{t.reload}</Button>
      </div>}
    </fieldset>
    {members.length >= 256 && <LeagueFeedback>{t.limit}</LeagueFeedback>}
    {dirty ? <LeagueFeedback>{t.unsaved}</LeagueFeedback> : <LeagueRoundPreview preview={roster.preview} />}
    {toast && <Toast {...toast} onClose={() => setToast(null)} />}
  </>;
}
export default function LeagueRosterEditor(props: Props) {
  const t = getLeagueRosterTexts();
  const { token, leagueId, divisionId } = props;
  const load = useCallback(() => getLeagueRoster(token, leagueId, divisionId), [token, leagueId, divisionId]);
  const resource = useLeagueResource(load);
  if (resource.loading) return <LeagueFeedback>{t.loading}</LeagueFeedback>;
  if (resource.error || !resource.data) return <LeagueLoadError error={resource.error || t.error} retry={resource.retry} />;
  return <RosterLoaded {...props} initial={resource.data} reload={resource.retry} />;
}
